// /join — create an account: email, nickname, Terms. We email a confirmation link; the account
// (with that nickname) exists once the link is opened. No password.
import { useState, type FormEvent } from "react";
import { checkNickname } from "@eb/shared";
import { ApiError } from "../api";
import { Link, useSearch, useTitle } from "../lib/router";
import { register } from "../session";

export function Join() {
  useTitle("Create account");
  const next = useSearch().get("next") ?? "/";
  const [email, setEmail] = useState("");
  const [nickname, setNickname] = useState("");
  const [terms, setTerms] = useState(false);
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState("");
  const policy = nickname.trim() ? checkNickname(nickname.trim()) : null;
  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (policy && !policy.ok) return setError(policy.reason ?? "Please choose another nickname.");
    setState("sending");
    try {
      await register(email, nickname.trim(), next);
      setState("sent");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
      setState("idle");
    }
  }
  return (
    <main id="main" className="page narrow">
      <h1>Create your account</h1>
      {state === "sent" ? (
        <div className="panel stack" role="status" data-testid="join-sent">
          <p>
            Almost there, <strong>{nickname.trim()}</strong>! We sent a confirmation link to <strong>{email}</strong>.
            Open it to finish — it works once and expires in 15 minutes.
          </p>
          {import.meta.env.DEV && (
            <p className="muted">
              Development: no mail is sent — open the <Link href="/dev/mailbox">dev mailbox</Link>.
            </p>
          )}
        </div>
      ) : (
        <form className="panel stack" onSubmit={submit}>
          <p>
            With an account you can write songs for the endless song, rate other people&apos;s songs and climb the{" "}
            <Link href="/fame">Hall of Fame</Link>. No password: we email you a link.
          </p>
          <label className="stack-label">
            <span>Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="join-email"
            />
          </label>
          <label className="stack-label">
            <span>Nickname (shown with your songs)</span>
            <input
              type="text"
              required
              maxLength={24}
              autoComplete="nickname"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              aria-invalid={policy ? !policy.ok : undefined}
              aria-describedby="join-nick-help"
              data-testid="join-nickname"
            />
            <span id="join-nick-help" className={`small ${policy && !policy.ok ? "error" : "muted"}`}>
              {policy && !policy.ok ? policy.reason : "3–24 letters, numbers, dots, dashes or underscores."}
            </span>
          </label>
          <label className="toggle">
            <input
              type="checkbox"
              required
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
              data-testid="join-terms"
            />
            <span>
              I accept the <Link href="/legal/terms">Terms</Link> and have read the{" "}
              <Link href="/legal/privacy">Privacy Policy</Link>.
            </span>
          </label>
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
          <button className="btn btn-primary" disabled={state === "sending" || !terms} data-testid="join-submit">
            {state === "sending" ? "Sending…" : "Create account"}
          </button>
          <p className="muted small">
            Already have an account? <Link href={`/signin?next=${encodeURIComponent(next)}`}>Sign in</Link>
          </p>
        </form>
      )}
    </main>
  );
}
